// RITA-1549 — Abschnitt „Aktionsprotokoll“ unten in der Karte „Vertragserstellung“ (Skizze 3c, Bild D; freigegeben
// von Julia am 20.10.2026), durch eine feine Linie abgetrennt wie die Entwurfszeile. Je Zeile links Datum und Uhrzeit,
// in der Mitte der Text (Entscheidung 7), rechts „Ansehen“ (alle Zeilen mit Datei) und „Erneut drucken“ (nur der
// neueste, nicht ersetzte Druck mit Vertrag und Datei, F6; Julia 21.10.2026: nur bei Druck-Einträgen mit Vertrag).
// „Erneut gedruckt“ steht eingerückt unter seinem Druck; eine gelöschte Datei bekommt die graue Unterzeile „Datei
// gelöscht“ ohne Knöpfe. Ohne Einträge erscheint kein Abschnitt. Der Server erzwingt „nicht ersetzt“ und „Datei
// vorhanden“ zusätzlich.
//
// RITA-1552 — Das Protokoll nach Verträgen (Skizze Rücklauf vom 21.10.2026, Bilder A und B; F6, F7, F10 „wie
// empfohlen“ freigegeben; E2, E4, E5 vom 22.10.2026). Es ersetzt die flache Liste aus 3c (F8) – ohne eigene
// Überschrift „Aktionsprotokoll“, denn die Skizze zeigt die Blöcke direkt unter den Knöpfen, jeder mit seinem Knopf
// „Aktionsprotokoll ausblenden ▴ / anzeigen ▾“:
// - Der neueste Vertrag steht oben, sein Protokoll ist offen.
// - Ältere Verträge stehen eingeklappt hinter „▸ n ältere Verträge · Einblenden“; danach je ein Block mit Stand und
//   geschlossenem Protokoll, darunter „▴ Ältere Verträge ausblenden“.
// - Ganz unten der Block „Ohne Vertrag aus dem PRM“ (E4): nicht in „n ältere Verträge“ mitgezählt, geschlossen, sobald
//   es Vertragsblöcke gibt, offen, wenn er der einzige Block ist.
// Neue Zeilenarten: „⬆ Unterschriebener Vertrag hochgeladen (Scan) · Name“ mit „Scan öffnen“ und „✉ Rücksendung
// gedruckt · Vorlage · Stand vom … · Name“ mit „Ansehen“ und „Erneut drucken“ (E2: jede Rücksendung); „Erneut gedruckt“
// steht auch unter einer Rücksendung eingerückt. „Ersetzt durch Vertrag vom …“ steht nur noch im Blockkopf (E5). Die
// 3c-Zeilentexte bleiben unverändert. Zuordnung, Status und „Erneut drucken“ entscheidet services/vertragsbloecke.ts.
// Auf- und Zuklappen bleibt auch während eines Drucks möglich; gesperrt sind nur die Knöpfe, die etwas abrufen.
import React, { useMemo, useState } from 'react';
import { Box, Button, Chip, Typography } from '@mui/material';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import PrintOutlinedIcon from '@mui/icons-material/PrintOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import type { Druckeintrag } from '../api/vertragsdruck';
import {
  aeltereAnzahl,
  erneutDruckbar,
  ohneVertragAnfangsOffen,
  Protokollzeile,
  vertragsbloecke,
  zeilenUnterzeilen,
} from '../services/vertragsbloecke';
import {
  AELTERE_AUSBLENDEN,
  aeltereVertraegeText,
  ANSEHEN,
  ERNEUT_DRUCKEN,
  erneutGedrucktText,
  gedrucktText,
  ruecksendungGedrucktText,
  SCAN_OEFFNEN,
  scanHochgeladenText,
  VORLAGE_INZWISCHEN_GEAENDERT,
} from '../services/vertragsdruckTexte';
import { entwurfszeit } from '../services/vertragsentwurfTexte';
import Vertragsblock from './Vertragsblock';

interface Props {
  eintraege: Druckeintrag[];
  /** Ein Druck, Erneut-Druck oder Abruf läuft: alle Knöpfe, die etwas abrufen, gesperrt. */
  laeuft: boolean;
  onAnsehen: (filename: string) => void;
  onErneutDrucken: (entryId: number) => void;
}

/** Schlüssel des Blocks „Ohne Vertrag aus dem PRM“ für den Auf-/Zu-Zustand (Vertragsblöcke: Kennung des Drucks). */
const OHNE_VERTRAG = 'ohne-vertrag';

/** Text der Zeile je Art; die 3c-Texte (Gedruckt, Erneut gedruckt) unverändert. */
function zeilentext(eintrag: Druckeintrag): string {
  if (eintrag.entry_type === 'reprinted') return erneutGedrucktText(eintrag.created_by_name);
  if (eintrag.entry_type === 'scan_uploaded') return scanHochgeladenText(eintrag.created_by_name);
  if (eintrag.entry_type === 'return_printed') return ruecksendungGedrucktText(eintrag);
  return gedrucktText(eintrag);
}

const knopfStil = { textTransform: 'none' } as const;

function Zeile({
  eintrag,
  eingerueckt,
  laeuft,
  onAnsehen,
  onErneutDrucken,
}: {
  eintrag: Druckeintrag;
  eingerueckt: boolean;
  laeuft: boolean;
  onAnsehen: (filename: string) => void;
  onErneutDrucken: (entryId: number) => void;
}) {
  const { filename } = eintrag;
  // Graue Unterzeile: nur noch „Datei gelöscht“ („Ersetzt durch …“ steht im Blockkopf, E5).
  const unterzeilen = zeilenUnterzeilen(eintrag);
  const scan = eintrag.entry_type === 'scan_uploaded';
  // F10: Der Scan hat „Scan öffnen“ statt „Ansehen“; beide nur mit Datei.
  const scanOeffnen = scan && !!filename;
  const ansehen = !scan && !!filename;
  const erneut = erneutDruckbar(eintrag);
  return (
    <Box
      data-testid="aktionsprotokoll-zeile"
      data-eintrag-id={eintrag.id}
      data-eintrag-art={eintrag.entry_type}
      data-eingerueckt={eingerueckt}
      sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, flexWrap: 'wrap', py: 0.75, pl: eingerueckt ? 4 : 0 }}
    >
      <Typography
        sx={{ fontSize: 13, color: 'text.secondary', flex: '0 0 auto', minWidth: 120, pt: 0.25 }}
        data-testid="aktionsprotokoll-zeit"
      >
        {entwurfszeit(eintrag.created_at)}
      </Typography>
      <Box sx={{ flex: '1 1 260px', minWidth: 0 }}>
        <Typography
          component="div"
          sx={{ fontSize: 14, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}
        >
          <span data-testid="aktionsprotokoll-text">{zeilentext(eintrag)}</span>
          {eintrag.template_changed && (
            <Chip
              size="small"
              color="warning"
              variant="outlined"
              label={VORLAGE_INZWISCHEN_GEAENDERT}
              data-testid="aktionsprotokoll-vorlage-geaendert"
            />
          )}
        </Typography>
        {unterzeilen.length > 0 && (
          <Box data-testid="aktionsprotokoll-zusatz">
            {unterzeilen.map((unterzeile) => (
              <Typography
                key={unterzeile}
                sx={{ fontSize: 13, color: 'text.disabled' }}
                data-testid="aktionsprotokoll-unterzeile"
              >
                {unterzeile}
              </Typography>
            ))}
          </Box>
        )}
      </Box>
      {(scanOeffnen || ansehen || erneut) && (
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {scanOeffnen && (
            <Button
              variant="outlined"
              color="inherit"
              size="small"
              startIcon={<DescriptionOutlinedIcon />}
              disabled={laeuft}
              onClick={() => onAnsehen(filename as string)}
              data-testid="aktionsprotokoll-scan-oeffnen"
              sx={knopfStil}
            >
              {SCAN_OEFFNEN}
            </Button>
          )}
          {ansehen && (
            <Button
              variant="outlined"
              color="inherit"
              size="small"
              startIcon={<VisibilityOutlinedIcon />}
              disabled={laeuft}
              onClick={() => onAnsehen(filename as string)}
              data-testid="aktionsprotokoll-ansehen"
              sx={knopfStil}
            >
              {ANSEHEN}
            </Button>
          )}
          {erneut && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<PrintOutlinedIcon />}
              disabled={laeuft}
              onClick={() => onErneutDrucken(eintrag.id)}
              data-testid="aktionsprotokoll-erneut-drucken"
              sx={knopfStil}
            >
              {ERNEUT_DRUCKEN}
            </Button>
          )}
        </Box>
      )}
    </Box>
  );
}

function Aktionsprotokoll({ eintraege, laeuft, onAnsehen, onErneutDrucken }: Props) {
  const gruppiert = useMemo(() => vertragsbloecke(eintraege), [eintraege]);
  // Nur die Blöcke, die jemand selbst auf- oder zugeklappt hat; alle anderen folgen der Vorgabe. So öffnet sich nach
  // einem neuen Vertragsdruck dessen Block von selbst, und der bisher neueste klappt zu.
  const [umgeschaltet, setUmgeschaltet] = useState<Record<string, boolean>>({});
  const [aeltereSichtbar, setAeltereSichtbar] = useState(false);
  if (eintraege.length === 0) return null;

  const [neuester, ...aeltere] = gruppiert.bloecke;
  const offen = (schluessel: string, vorgabe: boolean) => umgeschaltet[schluessel] ?? vorgabe;
  const umschalten = (schluessel: string, vorgabe: boolean) =>
    setUmgeschaltet((bisher) => ({ ...bisher, [schluessel]: !(bisher[schluessel] ?? vorgabe) }));

  const zeilen = (protokollzeilen: Protokollzeile[]) =>
    protokollzeilen.map(({ eintrag, kinder }) => (
      <React.Fragment key={eintrag.id}>
        <Zeile
          eintrag={eintrag}
          // Eine „Erneut gedruckt“-Zeile ohne bekannten Druck bleibt eingerückt wie in 3c.
          eingerueckt={eintrag.entry_type === 'reprinted'}
          laeuft={laeuft}
          onAnsehen={onAnsehen}
          onErneutDrucken={onErneutDrucken}
        />
        {kinder.map((kind) => (
          <Zeile
            key={kind.id}
            eintrag={kind}
            eingerueckt
            laeuft={laeuft}
            onAnsehen={onAnsehen}
            onErneutDrucken={onErneutDrucken}
          />
        ))}
      </React.Fragment>
    ));

  const ohneVertragVorgabe = ohneVertragAnfangsOffen(gruppiert);
  const umschaltzeile = (testId: string, text: string, onClick: () => void) => (
    <Box sx={{ borderTop: 1, borderColor: 'divider', mt: 2, pt: 1 }}>
      <Button
        variant="text"
        size="small"
        onClick={onClick}
        data-testid={testId}
        sx={{ textTransform: 'none', fontSize: 13, fontWeight: 600, minWidth: 0, px: 0.5 }}
      >
        {text}
      </Button>
    </Box>
  );

  return (
    <Box data-testid="aktionsprotokoll">
      {neuester && (
        <Vertragsblock
          block={neuester}
          offen={offen(String(neuester.kopf.id), true)}
          onUmschalten={() => umschalten(String(neuester.kopf.id), true)}
        >
          {zeilen(neuester.zeilen)}
        </Vertragsblock>
      )}
      {aeltere.length > 0 &&
        !aeltereSichtbar &&
        umschaltzeile('aeltere-vertraege-einblenden', aeltereVertraegeText(aeltereAnzahl(gruppiert)), () =>
          setAeltereSichtbar(true),
        )}
      {aeltere.length > 0 && aeltereSichtbar && (
        <>
          {aeltere.map((block) => (
            <Vertragsblock
              key={block.kopf.id}
              block={block}
              grau
              offen={offen(String(block.kopf.id), false)}
              onUmschalten={() => umschalten(String(block.kopf.id), false)}
            >
              {zeilen(block.zeilen)}
            </Vertragsblock>
          ))}
          {umschaltzeile('aeltere-vertraege-ausblenden', AELTERE_AUSBLENDEN, () => setAeltereSichtbar(false))}
        </>
      )}
      {gruppiert.ohneVertrag.length > 0 && (
        <Vertragsblock
          block={null}
          grau={!!neuester}
          offen={offen(OHNE_VERTRAG, ohneVertragVorgabe)}
          onUmschalten={() => umschalten(OHNE_VERTRAG, ohneVertragVorgabe)}
        >
          {zeilen(gruppiert.ohneVertrag)}
        </Vertragsblock>
      )}
    </Box>
  );
}

export default Aktionsprotokoll;
